import { RcaDaneValidationLabelType } from "../../../constants/RcaDaneValidationLabelTypes";
import { RcaDaneValidationLabelState } from "../../../constants/RcaDaneValidationLabelTypes";
import { IRcaDaneValidationDnsResponse } from "../../../models/IRcaDaneValidationDnsResponse";
import { IRcaDaneValidationDnsSecResult } from "../../../models/IRcaDaneValidationDnsSecResult";
import { IRcaDaneValidationLabelModel } from "../../../models/IRcaDaneValidationLabelModel";
import { IRcaDaneValidationMxRecordDetails } from "../../../models/IRcaDaneValidationMxRecordDetails";
import { IRcaDaneValidationTlsaDetails } from "../../../models/IRcaDaneValidationTlsaDetails";
import { RcaTestValidationStatus } from "../../../models/RcaTestValidationStatus";
import { DnsSecStatus } from "../../../models/IRcaDaneValidationDnsSecResult";
import { DNSSEC_AND_DANE_TEST, DNSSEC_ONLY_TEST } from "../../../constants/RcaDaneValidationTestTypes";

/**
 * Utility functions for the app
 */
export class Helpers {
  /**
   *
   * Get test labels for a specific DNSSEC status and best-match MX record from MX response
   *
   * @param record Preferred MxRecord to use for getting DNSSEC test status (optional)
   * @param dnsSecStatus best match DNSSEC status for these labels (from vanity domain, MX response or best match MX record)
   * @returns Test labels
   */
  public static getLabelsForMxResponse(
    record: IRcaDaneValidationMxRecordDetails | null,
    dnsSecStatus: IRcaDaneValidationDnsSecResult
  ): { [key in RcaDaneValidationLabelType]: IRcaDaneValidationLabelModel } {
    // DNS label success also for "Disabled" result
    const notFailure = dnsSecStatus.Status === DnsSecStatus.Success || dnsSecStatus.Status === DnsSecStatus.Disabled;
    const isDns: RcaDaneValidationLabelState =
      dnsSecStatus.Status === DnsSecStatus.Unknown
        ? RcaDaneValidationLabelState.Skipped
        : notFailure
        ? RcaDaneValidationLabelState.Success
        : RcaDaneValidationLabelState.Failed;
    const dnsTooltip =
      isDns === RcaDaneValidationLabelState.Failed
        ? dnsSecStatus.Details
        : isDns === RcaDaneValidationLabelState.Success
        ? WebsiteStrings.DaneDnsResolutionSucceeded
        : "";

    const isDnssec: RcaDaneValidationLabelState =
      dnsSecStatus.Status === DnsSecStatus.Unknown
        ? RcaDaneValidationLabelState.Skipped
        : dnsSecStatus.Status === DnsSecStatus.Disabled
        ? RcaDaneValidationLabelState.Warning
        : dnsSecStatus.Status === DnsSecStatus.Success
        ? RcaDaneValidationLabelState.Success
        : RcaDaneValidationLabelState.Failed;
    const dnsSecTooltip = dnsSecStatus.Details;

    const isTlsa: RcaDaneValidationLabelState =
      record === null // no best-match record case - failed to make MX query
        ? RcaDaneValidationLabelState.Skipped
        : record.TlsaRecordsResponse.Records.length === 0
        ? RcaDaneValidationLabelState.Warning
        : record.TlsaRecordsResponse.Records.some((tlsa: IRcaDaneValidationTlsaDetails) => tlsa.IsUsable)
        ? RcaDaneValidationLabelState.Success
        : RcaDaneValidationLabelState.Failed;
    const tlsaTooltip =
      isTlsa === RcaDaneValidationLabelState.Skipped
        ? WebsiteStrings.DaneTlsaRecordsQuerySkipped
        : isTlsa === RcaDaneValidationLabelState.Warning
        ? WebsiteStrings.DaneNoTlsaAvailable
        : isTlsa === RcaDaneValidationLabelState.Success
        ? WebsiteStrings.DaneUsableTlsaAvailable
        : WebsiteStrings.DaneOnlyUnusableTlsaAvailable;

    // DANE can be success, failed or non-performed
    const isDane: RcaDaneValidationLabelState =
      record === null || record.DaneResult === null
        ? RcaDaneValidationLabelState.Skipped
        : record.DaneResult.Status === RcaTestValidationStatus.Warning
        ? RcaDaneValidationLabelState.Warning
        : record.DaneResult.Status === RcaTestValidationStatus.Failed
        ? RcaDaneValidationLabelState.Failed
        : record.DaneResult.Status === RcaTestValidationStatus.Success
        ? RcaDaneValidationLabelState.Success
        : RcaDaneValidationLabelState.Skipped;

    const daneTooltip =
      record === null || record.DaneResult === null ? WebsiteStrings.DaneValidationNotPerformed : record.DaneResult.Details;

    return {
      DNS: { state: isDns, tooltip: dnsTooltip },
      DNSSEC: { state: isDnssec, tooltip: dnsSecTooltip },
      TLSA: { state: isTlsa, tooltip: tlsaTooltip },
      DANE: { state: isDane, tooltip: daneTooltip },
    };
  }

  /**
   *
   * Transform MX record labels into summary message for baseline DNS only test.
   *
   * @param labels - An array of MX labels
   * @returns - The summary message
   */
  public static getSummaryMessageForMxRecordLabelBaselineDns(labels: {
    [key in RcaDaneValidationLabelType]: IRcaDaneValidationLabelModel;
  }): string {
    const variant = [labels.DNS]
      .map((m) =>
        m.state === RcaDaneValidationLabelState.Warning
          ? "!"
          : m.state === RcaDaneValidationLabelState.Skipped
          ? "-"
          : m.state === RcaDaneValidationLabelState.Success
          ? "V"
          : "X"
      )
      .join("|");
    const unexpectedSummary = WebsiteStrings.DaneUnexpectedResults;
    switch (variant) {
      case "X":
        return `${WebsiteStrings.DaneEmailWillNotBeDelivered} ${labels.DNS.tooltip}`;
      case "V":
        return WebsiteStrings.DaneEmailWillBeDelivered;
      case "!":
        return `${WebsiteStrings.DaneEmailWillBeDelivered} ${labels.DNS.tooltip}`;
      case "-":
        return `${WebsiteStrings.DaneEmailWillNotBeDelivered} ${labels.DNS.tooltip}`;
      default:
        return unexpectedSummary;
    }
  }

  /**
   *
   * Transform MX record labels into summary message for DNSSEC only test.
   *
   * @param labels - An array of MX labels
   * @returns - The summary message
   */
  public static getSummaryMessageForMxRecordLabelDnsSecOnly(labels: {
    [key in RcaDaneValidationLabelType]: IRcaDaneValidationLabelModel;
  }): string {
    const variant = [labels.DNSSEC]
      .map((m) =>
        m.state === RcaDaneValidationLabelState.Warning
          ? "!"
          : m.state === RcaDaneValidationLabelState.Skipped
          ? "-"
          : m.state === RcaDaneValidationLabelState.Success
          ? "V"
          : "X"
      )
      .join("|");
    const unexpectedSummary = WebsiteStrings.DaneUnexpectedResults;
    switch (variant) {
      case "X":
        return `${WebsiteStrings.DaneEmailWillNotBeDelivered} ${labels.DNSSEC.tooltip}`;
      case "V":
        return WebsiteStrings.DaneEmailWillBeDelivered;
      case "!":
        return `${WebsiteStrings.DaneEmailWillBeDelivered} ${labels.DNSSEC.tooltip}`;
      case "-":
        return `${WebsiteStrings.DaneEmailWillNotBeDelivered} ${labels.DNSSEC.tooltip}`;
      default:
        return unexpectedSummary;
    }
  }

  /**
   *
   * Transform MX record label into summary message.
   *
   * @param labels - An array of MX labels
   * @returns - The summary message
   */
  public static getSummaryMessageForMxRecordLabel(labels: { [key in RcaDaneValidationLabelType]: IRcaDaneValidationLabelModel }): string {
    const variant = [labels.DNSSEC, labels.TLSA, labels.DANE]
      .map((m) =>
        m.state === RcaDaneValidationLabelState.Warning
          ? "!"
          : m.state === RcaDaneValidationLabelState.Skipped
          ? "-"
          : m.state === RcaDaneValidationLabelState.Success
          ? "V"
          : "X"
      )
      .join("|");
    const unexpectedSummary = WebsiteStrings.DaneUnexpectedResults;
    switch (variant) {
      case "X|-|-":
      case "X|!|!":
      case "X|X|-":
      case "X|V|X":
      case "X|V|V":
        return `${WebsiteStrings.DaneEmailWillNotBeDelivered} ${labels.DNSSEC.tooltip}`;
      case "-|-|-":
      case "-|!|!":
        return `${WebsiteStrings.DaneEmailNotDeliveredNoSmtpServers} ${labels.DNSSEC.tooltip}`;
      case "!|!|!":
      case "!|-|-":
      case "!|X|-":
      case "!|V|X":
      case "!|V|V":
        return `${WebsiteStrings.DaneSkippedEmailWillBeSent} ${labels.DNSSEC.tooltip}`;
      case "V|!|!":
        return `${WebsiteStrings.DaneSkippedEmailWillBeSent} ${labels.TLSA.tooltip}`;
      case "V|-|-":
      case "V|X|-":
        return `${WebsiteStrings.DaneSkippedEmailWillBeSent} ${labels.TLSA.tooltip}`;
      case "V|V|X":
        return `${WebsiteStrings.DaneEmailWillNotBeDelivered} ${WebsiteStrings.DaneDeclared} ${labels.DANE.tooltip}`;
      case "V|V|V":
        return `${WebsiteStrings.DaneEmailWillBeDelivered} ${WebsiteStrings.DaneDeclared} ${labels.DANE.tooltip}`;
      default:
        return unexpectedSummary;
    }
  }

  /**
   *
   * Transform MX record label into summary message.
   *
   * @param testType - test type
   * @param labels - An array of MX labels
   * @returns - The summary message
   */
  public static getSummaryTextFromLabels(
    testType: string,
    labels: { [key in RcaDaneValidationLabelType]: IRcaDaneValidationLabelModel }
  ): string {
    return testType === DNSSEC_AND_DANE_TEST
      ? Helpers.getSummaryMessageForMxRecordLabel(labels)
      : testType === DNSSEC_ONLY_TEST
      ? Helpers.getSummaryMessageForMxRecordLabelDnsSecOnly(labels)
      : Helpers.getSummaryMessageForMxRecordLabelBaselineDns(labels);
  }

  /**
   *
   * Get summary message for MX response
   *
   * @param testType - test type
   * @param response - MX response
   * @param record - best-match MX record (if available) or null
   * @param domainDnssecStatus - dnssecStatus of vanity domain (if available) or null
   * @returns - The summary message
   */
  public static getSummaryText(
    testType: string,
    response: IRcaDaneValidationDnsResponse<IRcaDaneValidationMxRecordDetails>,
    record: IRcaDaneValidationMxRecordDetails | null,
    domainDnssecStatus: IRcaDaneValidationDnsSecResult | null
  ): string {
    let dnsSecStatus = record === null ? response.DnsSecStatus : record.DnsSecStatus;
    dnsSecStatus = domainDnssecStatus === null || domainDnssecStatus.Status === DnsSecStatus.Success ? dnsSecStatus : domainDnssecStatus;

    const labels = () => Helpers.getLabelsForMxResponse(record, dnsSecStatus);

    return record === null
      ? `${WebsiteStrings.DaneEmailNotDeliveredNoSmtpServers} ${response.DnsSecStatus.Details}`
      : Helpers.getSummaryTextFromLabels(testType, labels());
  }

  /**
   * Groups an array of items by a particular value
   *
   * @param items - The items to group
   * @param selector - A selector function to return the string to group by
   * @returns - The grouped items
   */
  public static groupBy<T>(items: T[], selector: (t: T) => string): { [key: string]: T[] } {
    const initial: { [key: string]: T[] } = {};
    return items.reduce((rv, x) => {
      const key = selector(x);
      (rv[key] = rv[key] || []).push(x);
      return rv;
    }, initial);
  }
}
